import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, ExternalLink, Copy, Trash2, CheckCircle, Loader2, Eye, EyeOff, History, Pencil, Archive, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { QuoteRevisionHistory } from "./QuoteRevisionHistory";
import { ChangeReasonDialog } from "./ChangeReasonDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { PRODUCTION_DOMAIN } from "@/lib/constants";
import { EditQuotePaymentDialog } from "./EditQuotePaymentDialog";
import { QuotePaymentCorrectionHistory } from "./QuotePaymentCorrectionHistory";
import { formatCurrency } from "@/lib/currencyUtils";
import { GenerateProgrammeDialog } from "./GenerateProgrammeDialog";
import { GenerateClientProgrammeDialog } from "./GenerateClientProgrammeDialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { costPresentationLabel } from "@/lib/quoteLabelUtils";
import { PreparedForInlineEdit } from "./PreparedForInlineEdit";
import { QuoteLabelInlineEdit } from "./QuoteLabelInlineEdit";
import { QuoteComparisonDialog } from "./QuoteComparisonDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { buildQuoteAutoLabel } from "@/lib/quoteLabelUtils";
import { Columns3 } from "lucide-react";
import { useAcceptedTerms, AcceptedTermsDialog, type AcceptedTermsRecord } from "@/components/AcceptedTermsCard";
import { HotelOptionMarginsCard } from "@/components/quote/HotelOptionMarginsCard";

interface QuoteViewerProps {
  leadId: string;
  currency: string;
  leadStatus: string;
  acceptedQuotationId?: string | null;
  lead?: any;
}

export const QuoteViewer = ({ leadId, currency, leadStatus, acceptedQuotationId, lead }: QuoteViewerProps) => {
  const { data: signedTerms } = useAcceptedTerms(leadId);
  const [signedOpen, setSignedOpen] = useState<AcceptedTermsRecord | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState<{ id: string; number: string } | null>(null);
  const [setAcceptedDialogOpen, setSetAcceptedDialogOpen] = useState(false);
  const [quoteToSetAccepted, setQuoteToSetAccepted] = useState<{ id: string; number: string } | null>(null);

  // Payment dialog state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentDialogQuote, setPaymentDialogQuote] = useState<any>(null);
  const [paymentDialogMode, setPaymentDialogMode] = useState<"record" | "add" | "edit">("record");
  const [correctionHistoryOpen, setCorrectionHistoryOpen] = useState(false);
  const [correctionHistoryQuote, setCorrectionHistoryQuote] = useState<{ id: string; number: string } | null>(null);

  // Programme dialog state
  const [programmeDialogOpen, setProgrammeDialogOpen] = useState(false);
  const [programmeDialogQuoteId, setProgrammeDialogQuoteId] = useState<string | null>(null);
  const [brandedQuoteToken, setBrandedQuoteToken] = useState<string | null>(null);

  // Comparison state
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState<string[]>([]);
  const [comparisonOpen, setComparisonOpen] = useState(false);

  const toggleCompareSelection = (quoteId: string) => {
    setSelectedForCompare((prev) =>
      prev.includes(quoteId) ? prev.filter((id) => id !== quoteId) : [...prev, quoteId]
    );
  };

  const showProgrammeButton = ['quote_sent', 'quote_accepted', 'amendment', 'invoicing', 'executing', 'reporting', 'completed'].includes(leadStatus);

  const [showArchived, setShowArchived] = useState(false);
  const { data: allQuotations } = useQuery({
    queryKey: ["quotations", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quotations")
        .select("*")
        .eq("lead_id", leadId)
        .eq("document_type", "quote")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });
  const quotations = (allQuotations || []).filter((q: any) => !q.archived_at);
  const archivedQuotes = (allQuotations || []).filter((q: any) => !!q.archived_at);

  // Fetch participant options to display labels on quotes
  const { data: participantOptions } = useQuery({
    queryKey: ["participant-options-for-quotes", leadId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("lead_participant_options")
        .select("id, label, quantity")
        .eq("lead_id", leadId);
      if (error) throw error;
      return data as { id: string; label: string; quantity: number }[];
    },
  });

  // Fetch view stats for all quotations
  const quotationIds = quotations?.map(q => q.id) || [];
  const { data: viewStats } = useQuery({
    queryKey: ["quote-view-stats", quotationIds.join(",")],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_quote_view_stats", {
        p_quotation_ids: quotationIds,
      });
      if (error) throw error;
      const statsMap: Record<string, { view_count: number; first_viewed_at: string; last_viewed_at: string }> = {};
      if (Array.isArray(data)) {
        (data as any[]).forEach((s: any) => {
          statsMap[s.quotation_id] = s;
        });
      }
      return statsMap;
    },
    enabled: quotationIds.length > 0,
  });

  // Fetch correction counts for badges
  const { data: correctionCounts } = useQuery({
    queryKey: ["quote-payment-correction-counts", quotationIds.join(",")],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quote_payment_corrections")
        .select("quotation_id")
        .in("quotation_id", quotationIds);
      if (error) throw error;
      const counts: Record<string, number> = {};
      (data || []).forEach(r => {
        counts[r.quotation_id] = (counts[r.quotation_id] || 0) + 1;
      });
      return counts;
    },
    enabled: quotationIds.length > 0,
  });

  // Quotes are archived, never erased: hidden from the lead, client link disabled, restorable.
  const deleteQuoteMutation = useMutation({
    mutationFn: async ({ quoteId, reason }: { quoteId: string; reason: string }) => {
      const { error } = await (supabase as any).rpc("archive_quotation", {
        p_quotation_id: quoteId,
        p_reason: reason,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quotations", leadId] });
      toast.success("Quote archived — you can restore it from Archived quotes");
      setDeleteDialogOpen(false);
      setQuoteToDelete(null);
    },
    onError: (error: any) => {
      console.error("Archive quote error:", error);
      toast.error(error?.message || "Failed to archive quote");
    },
  });

  const restoreQuoteMutation = useMutation({
    mutationFn: async (quoteId: string) => {
      const { error } = await (supabase as any).rpc("restore_quotation", { p_quotation_id: quoteId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quotations", leadId] });
      toast.success("Quote restored — its client link works again");
    },
    onError: (error: any) => {
      console.error("Restore quote error:", error);
      toast.error(error?.message || "Failed to restore quote");
    },
  });

  const setAcceptedMutation = useMutation({
    mutationFn: async (quoteId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error: leadError } = await supabase
        .from("leads")
        .update({ accepted_quotation_id: quoteId, status: "quote_accepted" })
        .eq("id", leadId);
      if (leadError) throw leadError;

      const { error: quoteError } = await supabase
        .from("quotations")
        .update({ status: "accepted" })
        .eq("id", quoteId);
      if (quoteError) throw quoteError;

      const { data: quote } = await supabase
        .from("quotations")
        .select("quote_number")
        .eq("id", quoteId)
        .single();

      await supabase.from("activity_log").insert({
        user_id: user.id,
        lead_id: leadId,
        action: "SET_ACCEPTED_QUOTE",
        details: `Set ${quote?.quote_number} as the accepted quote option`,
      });

      await supabase.from("lead_changes").insert({
        lead_id: leadId,
        user_id: user.id,
        field_changed: "accepted_quotation_id",
        old_value: acceptedQuotationId || null,
        new_value: quoteId,
        change_source: "quote_acceptance",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quotations", leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      toast.success("Quote set as accepted - execution will now use this quote's items");
      setSetAcceptedDialogOpen(false);
      setQuoteToSetAccepted(null);
    },
    onError: (error) => {
      console.error("Set accepted error:", error);
      toast.error("Failed to set accepted quote");
    },
  });

  if (!allQuotations || allQuotations.length === 0) {
    return null;
  }

  const handleDeleteClick = (quoteId: string, quoteNumber: string) => {
    setQuoteToDelete({ id: quoteId, number: quoteNumber });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async (reason: string) => {
    if (!quoteToDelete) return;
    deleteQuoteMutation.mutate({ quoteId: quoteToDelete.id, reason });
  };

  const handleCopyLink = (shareToken: string | null, quoteNumber: string) => {
    if (!shareToken) {
      toast.error("Share token not available for this quote");
      return;
    }
    const link = `${PRODUCTION_DOMAIN}/quote/${shareToken}`;
    navigator.clipboard.writeText(link);
    toast.success(`Link copied for ${quoteNumber}!`);
  };

  const handleSetAcceptedClick = (quoteId: string, quoteNumber: string) => {
    setQuoteToSetAccepted({ id: quoteId, number: quoteNumber });
    setSetAcceptedDialogOpen(true);
  };

  const handlePaymentClick = (quote: any, mode: "record" | "add" | "edit") => {
    setPaymentDialogQuote(quote);
    setPaymentDialogMode(mode);
    setPaymentDialogOpen(true);
  };

  const handleCorrectionHistoryClick = (quoteId: string, quoteNumber: string) => {
    setCorrectionHistoryQuote({ id: quoteId, number: quoteNumber });
    setCorrectionHistoryOpen(true);
  };

  return (
    <>
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <h2 className="text-lg font-semibold">Generated Quotes</h2>
          <div className="flex items-center gap-2">
            {compareMode && (
              <>
                <span className="text-xs text-muted-foreground">
                  {selectedForCompare.length} selected
                </span>
                <Button
                  size="sm"
                  disabled={selectedForCompare.length < 2}
                  onClick={() => setComparisonOpen(true)}
                >
                  Compare {selectedForCompare.length >= 2 ? `(${selectedForCompare.length})` : ""}
                </Button>
              </>
            )}
            <Button
              size="sm"
              variant={compareMode ? "ghost" : "outline"}
              onClick={() => {
                setCompareMode((v) => !v);
                setSelectedForCompare([]);
              }}
            >
              <Columns3 className="h-4 w-4 mr-1" />
              {compareMode ? "Cancel" : "Compare options"}
            </Button>
          </div>
        </div>
        <div className="space-y-3">
          {quotations.map((quote) => {
            const isAccepted = acceptedQuotationId === quote.id;
            const hasCorrectionHistory = (correctionCounts?.[quote.id] || 0) > 0;
            const quoteOption =
              participantOptions?.find((o: any) => o.id === (quote as any).participant_option_id) || null;
            const autoLabel = buildQuoteAutoLabel(quote, {
              option: quoteOption,
              lead,
              includeNumber: false,
            });
            return (
            <div key={quote.id} className="space-y-2">
            <div
              className={`flex flex-col gap-2 w-full p-4 rounded-lg ${isAccepted ? 'bg-green-500/10 border border-green-500/30' : 'bg-muted'}`}
            >
              {compareMode && (
                <div className="order-0">
                  <Checkbox
                    checked={selectedForCompare.includes(quote.id)}
                    onCheckedChange={() => toggleCompareSelection(quote.id)}
                    aria-label={`Select quote ${quote.quote_number} for comparison`}
                  />
                </div>
              )}
              <div className="contents">
                <div className="order-1 flex items-center gap-2 flex-wrap [&>*]:whitespace-nowrap [&>*]:shrink-0">
                  <FileText className="h-4 w-4 text-primary" />
                  <span className="font-medium">Quote #{quote.quote_number}</span>
                  {isAccepted && (
                    <Badge className="bg-green-600 text-white">✓ Accepted</Badge>
                  )}
                  {(() => {
                    const rec = signedTerms?.find((r) => r.id === quote.id);
                    return rec ? (
                      <Button variant="link" size="sm" className="h-auto p-0" onClick={() => setSignedOpen(rec)}>
                        View signed terms
                      </Button>
                    ) : null;
                  })()}
                  {quote.payment_status === "paid" && (
                    <Badge className="bg-blue-600 text-white">Paid</Badge>
                  )}
                    {quote.payment_status === "partial" && (
                      <Badge className="bg-amber-500 text-white">Part Paid</Badge>
                    )}
                    {(() => {
                      const optionId = (quote as any).participant_option_id;
                      const opt = optionId && participantOptions?.find((o: any) => o.id === optionId);
                      if (opt) return <Badge variant="secondary">{opt.label}: {opt.quantity} pax</Badge>;
                      if (quote.pax_count) return <Badge variant="outline">{quote.pax_count} pax</Badge>;
                      return null;
                    })()}
                    {(() => {
                      const p = costPresentationLabel(quote as any);
                      return (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline">{p.label}</Badge>
                            </TooltipTrigger>
                            <TooltipContent>{p.description}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })()}
                </div>
                <div className="order-3 text-justify">
                <QuoteLabelInlineEdit
                  quotationId={quote.id}
                  value={(quote as any).quote_label}
                  autoLabel={autoLabel}
                  invalidateKeys={[["quotations", leadId]]}
                />
                </div>
                <div className="order-4 flex flex-col gap-0.5">
                <div className="flex flex-wrap items-baseline gap-x-3">
                  <span className="text-sm font-semibold">
                    Total: {formatCurrency(quote.total_amount, currency)}
                  </span>
                  {(quote as any).payment_link_url && (() => {
                    const linkAmt = (quote as any).payment_link_amount != null ? Number((quote as any).payment_link_amount) : Number(quote.total_amount);
                    const differs = Math.abs(linkAmt - Number(quote.total_amount)) >= 0.01;
                    return (
                      <span className="text-xs text-muted-foreground">
                        Pay link: <span className={differs ? "font-semibold text-primary" : ""}>{formatCurrency(linkAmt, currency)}</span>
                        {differs && " · updated to client's selection"}
                      </span>
                    );
                  })()}
                </div>
                {(quote.payment_status === "partial" || (quote.payment_status === "paid" && quote.payment_amount != null)) && (
                  <p className="text-sm mt-0.5">
                    <span className="text-muted-foreground">Received: </span>
                    <span className={quote.payment_status === "partial" ? "text-amber-600 font-medium" : "font-medium"}>
                      {formatCurrency(quote.payment_amount || 0, currency)}
                    </span>
                    {quote.payment_status === "partial" && (
                      <span className="text-muted-foreground"> of {formatCurrency(quote.total_amount, currency)}</span>
                    )}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs text-muted-foreground [&>*]:mt-0">
                  <span>Created: {format(new Date(quote.created_at), "MMM d, yyyy")}</span>
                  <PreparedForInlineEdit
                    quotationId={quote.id}
                    value={(quote as any).prepared_for}
                    fallbackName={lead?.client_name}
                    invalidateKeys={[["quotations", leadId]]}
                  />
                  {viewStats?.[quote.id] ? (
                    <span className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      Viewed {viewStats[quote.id].view_count} time{viewStats[quote.id].view_count !== 1 ? 's' : ''} — last seen {formatDistanceToNow(new Date(viewStats[quote.id].last_viewed_at), { addSuffix: true })}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-muted-foreground/50">
                      <EyeOff className="h-3 w-3" />
                      Not yet viewed
                    </span>
                  )}
                </div>
                </div>
              </div>
              <div className="order-2 flex gap-1.5 flex-wrap items-center w-full">
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => window.open(`/quote/${quote.share_token}`, '_blank', 'noopener,noreferrer')}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>View Quote</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleCopyLink(quote.share_token, quote.quote_number)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Copy Link</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {showProgrammeButton && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setProgrammeDialogQuoteId(quote.id);
                      setProgrammeDialogOpen(true);
                    }}
                    className="gap-1.5 h-8"
                  >
                    <FileText className="h-4 w-4" />
                    Programme
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBrandedQuoteToken(quote.share_token)}
                  className="gap-1.5 h-8"
                >
                  <FileText className="h-4 w-4" />
                  Branded Quote
                </Button>
                {quotations.length > 1 && !isAccepted && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSetAcceptedClick(quote.id, quote.quote_number)}
                    className="gap-2 h-8"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Set as Accepted
                  </Button>
                )}
                {/* Record Payment for accepted quotes with no payment yet */}
                {isAccepted && !quote.payment_status && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handlePaymentClick(quote, "record")}
                    className="gap-2 h-8 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Record Payment
                  </Button>
                )}
                {/* Add Payment for partially paid quotes */}
                {isAccepted && quote.payment_status === "partial" && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handlePaymentClick(quote, "add")}
                    className="gap-2 h-8 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Add Payment
                  </Button>
                )}
                {/* Edit Payment for paid or partial quotes (correction) */}
                {isAccepted && (quote.payment_status === "paid" || quote.payment_status === "partial") && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePaymentClick(quote, "edit")}
                    className="gap-2 h-8"
                  >
                    <Pencil className="h-4 w-4" />
                    Edit Payment
                  </Button>
                )}
                {hasCorrectionHistory && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCorrectionHistoryClick(quote.id, quote.quote_number)}
                    className="gap-2 h-8"
                  >
                    <History className="h-4 w-4" />
                    History
                  </Button>
                )}
                {!isAccepted && (
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Archive quote"
                    aria-label="Archive quote"
                    onClick={() => handleDeleteClick(quote.id, quote.quote_number)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 ml-auto"
                  >
                    <Archive className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            {Array.isArray((quote as any).itinerary_snapshot) && (
              <HotelOptionMarginsCard
                items={(quote as any).itinerary_snapshot}
                currency={(quote as any).currency || "ZAR"}
                priceOpts={{
                  markupPct: Number(quote.markup_percentage) || 0,
                  transportDiscount: Number((quote as any).transport_discount_amount) || 0,
                  discountPct: Number((quote as any).discount_percentage) || 0,
                  discountFixed: Number((quote as any).discount_fixed_amount) || 0,
                  feesTotal: (c: number) =>
                    (((quote as any).additional_fees as any[]) || []).reduce(
                      (s, f) => s + c * ((Number(f?.percentage) || 0) / 100),
                      0
                    ),
                }}
              />
            )}
            </div>
          );
          })}
          {quotations.length === 0 && (
            <p className="text-sm text-muted-foreground">No active quotes. Archived quotes are listed below.</p>
          )}
        </div>
        {archivedQuotes.length > 0 && (
          <div className="mt-4 border-t pt-3">
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 h-8 px-2 text-muted-foreground"
              onClick={() => setShowArchived((v) => !v)}
            >
              <Archive className="h-4 w-4" />
              Archived quotes ({archivedQuotes.length})
              {showArchived ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            {showArchived && (
              <div className="mt-2 space-y-2">
                {archivedQuotes.map((q: any) => (
                  <div
                    key={q.id}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-dashed bg-muted/40 px-3 py-2"
                  >
                    <span className="font-medium text-sm whitespace-nowrap">#{q.quote_number}</span>
                    <span className="text-sm whitespace-nowrap">{formatCurrency(Number(q.total_amount) || 0, currency)}</span>
                    <span className="text-xs text-muted-foreground min-w-0 flex-1 break-words">
                      Created {format(new Date(q.created_at), "d MMM yyyy")} · Archived{" "}
                      {format(new Date(q.archived_at), "d MMM yyyy, HH:mm")}
                      {q.archived_reason ? ` · ${q.archived_reason}` : ""}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 h-8 ml-auto"
                      disabled={restoreQuoteMutation.isPending}
                      onClick={() => restoreQuoteMutation.mutate(q.id)}
                    >
                      <RotateCcw className="h-4 w-4" />
                      Restore
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      <ChangeReasonDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        title="Archive this quote?"
        description={`${quoteToDelete?.number || 'This quote'} will be hidden from the lead and its client link will stop working. You can restore it any time from Archived quotes. Please explain why.`}
      />

      {/* Set as Accepted confirmation dialog */}
      <AlertDialog open={setAcceptedDialogOpen} onOpenChange={setSetAcceptedDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Set as Accepted Quote</AlertDialogTitle>
            <AlertDialogDescription>
              This will set {quoteToSetAccepted?.number} as the accepted quote option for this lead.
              <br /><br />
              <strong>What this means:</strong>
              <ul className="list-disc ml-4 mt-2">
                <li>The lead status will change to "Quote Accepted"</li>
                <li>Only items from this quote will appear in execution</li>
                <li>Other quote options will remain for reference</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => quoteToSetAccepted && setAcceptedMutation.mutate(quoteToSetAccepted.id)}
              className="bg-green-600 hover:bg-green-700"
              disabled={setAcceptedMutation.isPending}
            >
              {setAcceptedMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                "Set as Accepted"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Payment recording/amendment dialog */}
      {paymentDialogQuote && (
        <EditQuotePaymentDialog
          open={paymentDialogOpen}
          onOpenChange={setPaymentDialogOpen}
          quoteId={paymentDialogQuote.id}
          quoteNumber={paymentDialogQuote.quote_number}
          totalAmount={paymentDialogQuote.total_amount}
          currentPaymentStatus={paymentDialogQuote.payment_status}
          currentPaymentAmount={paymentDialogQuote.payment_amount}
          currentPaymentReference={paymentDialogQuote.payment_reference}
          leadId={leadId}
          leadStatus={leadStatus}
          currency={currency}
          mode={paymentDialogMode}
        />
      )}

      {/* Payment correction history dialog */}
      {correctionHistoryQuote && (
        <QuotePaymentCorrectionHistory
          open={correctionHistoryOpen}
          onOpenChange={setCorrectionHistoryOpen}
          quotationId={correctionHistoryQuote.id}
          quoteNumber={correctionHistoryQuote.number}
          currency={currency}
        />
      )}

      {/* Side-by-side comparison */}
      {comparisonOpen && (
        <QuoteComparisonDialog
          open={comparisonOpen}
          onOpenChange={setComparisonOpen}
          quotes={quotations.filter((q) => selectedForCompare.includes(q.id))}
          participantOptions={participantOptions || []}
          lead={lead}
          leadId={leadId}
          currency={currency}
          acceptedQuotationId={acceptedQuotationId}
          onSetAccepted={(id, number) => {
            setComparisonOpen(false);
            handleSetAcceptedClick(id, number);
          }}
        />
      )}

      {brandedQuoteToken && (
        <GenerateClientProgrammeDialog
          open={!!brandedQuoteToken}
          onOpenChange={(o) => !o && setBrandedQuoteToken(null)}
          quoteShareToken={brandedQuoteToken}
          kind="quote"
        />
      )}

      {/* Generate Programme Dialog */}
      {programmeDialogQuoteId && lead && (
        <GenerateProgrammeDialog
          open={programmeDialogOpen}
          onOpenChange={setProgrammeDialogOpen}
          leadId={leadId}
          lead={lead}
          acceptedQuotationId={programmeDialogQuoteId}
        />
      )}

      {/* Show revision history for accepted quotes */}
      {(leadStatus === "quote_accepted" || leadStatus === "invoicing" || leadStatus === "executing" || leadStatus === "reporting" || leadStatus === "completed") && quotations.length > 0 && (
        <QuoteRevisionHistory 
          quotationId={acceptedQuotationId || quotations[0].id} 
          currency={currency}
        />
      )}
      <AcceptedTermsDialog record={signedOpen} open={!!signedOpen} onOpenChange={(o) => !o && setSignedOpen(null)} />
    </>
  );
};
